import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import {
    OTHER_SUPPORT_VALUE,
    RETIRED_SUPPORT_NEEDS,
    SUPPORT_NEED_GROUPS,
} from './supportNeedGroups';

import type { CareerAspirationFormValues } from './careerAspirationValidation';
import type { SupportNeed } from '@/types/career-growth';
import type { OptionGroup, OptionProps } from '@amelio/ui-library';

import { toggleInList } from '@/components/ChoiceChips/ChoiceChips';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';

/** Where a need no longer offered, but recorded on the answer, is listed. */
const UNCATEGORIZED_GROUP_ID = 'other';
const CUSTOM_GROUP_ID = 'custom';

/**
 * The support needs question as one multiple choice: the needs on offer by group, "Other" last, and what
 * picking each one does to the form.
 */
export const useSupportNeedsChoice = () => {
    const { t } = useAmelioTranslations();
    const { getManagerLabel } = useCompanyInformation();
    const {
        watch,
        getValues,
        setValue,
        clearErrors,
        formState: { defaultValues },
    } = useFormContext<CareerAspirationFormValues>();

    const supportNeeds = watch('supportNeeds');
    const hasOtherSupport = watch('hasOtherSupport');

    const managerLabel = getManagerLabel();
    // A need no longer offered stays on the list of an answer that was recorded with it, so it can
    // be unpicked -- and picked again, since it is read from the answer, not from the live choice.
    // Keyed by content: the form hands back a new copy of the recorded answer on every change.
    const retiredNeedsRecordedKey = RETIRED_SUPPORT_NEEDS.filter((need) =>
        defaultValues?.supportNeeds?.includes(need),
    ).join();

    // The list keeps its active option in view whenever it receives new options: rebuilt on every
    // render, they sent it back to the top after each pick.
    const groups = useMemo((): OptionGroup[] => {
        const retiredNeedsRecorded = RETIRED_SUPPORT_NEEDS.filter((need) =>
            retiredNeedsRecordedKey.split(',').includes(need),
        );
        const needOption = (need: SupportNeed): OptionProps => ({
            value: need,
            label: t(`performanceProfile.growth.aspiration.supportNeedOptions.${need}`, {
                managerLabel,
            }),
        });

        // Grouped the way the meeting asks them; "Other" stands in its own group, last.
        return [
            ...SUPPORT_NEED_GROUPS.map(({ id, needs }) => ({
                id,
                label: t(`performanceProfile.growth.aspiration.supportNeedGroups.${id}`),
                options: [
                    ...needs.map(needOption),
                    ...(id === UNCATEGORIZED_GROUP_ID ? retiredNeedsRecorded.map(needOption) : []),
                ],
            })),
            {
                id: CUSTOM_GROUP_ID,
                label: t('performanceProfile.growth.aspiration.supportNeedGroups.custom'),
                options: [
                    {
                        value: OTHER_SUPPORT_VALUE,
                        label: t(
                            'performanceProfile.growth.aspiration.form.supportNeedsOtherChoice',
                        ),
                    },
                ],
            },
        ];
    }, [t, managerLabel, retiredNeedsRecordedKey]);

    // Read from the form, not from this render: "Clear all" flips every picked need in one go, and
    // each flip must see the ones before it.
    const toggle = (value: string) => {
        if (value === OTHER_SUPPORT_VALUE) {
            const isOtherPicked = getValues('hasOtherSupport');
            // Unpicking "Other" drops what was written for it, and the complaint about it: picking it
            // again starts blank, with nothing to fix until the next save.
            if (isOtherPicked) {
                setValue('supportNeedsOther', '', { shouldDirty: true });
                clearErrors('supportNeedsOther');
            }
            setValue('hasOtherSupport', !isOtherPicked, { shouldDirty: true });
            return;
        }
        setValue('supportNeeds', toggleInList(getValues('supportNeeds'), value as SupportNeed), {
            shouldDirty: true,
        });
    };

    return {
        groups,
        selected: hasOtherSupport ? [...supportNeeds, OTHER_SUPPORT_VALUE] : supportNeeds,
        hasOtherSupport,
        toggle,
    };
};
